import React, { useState, useEffect, useCallback, useRef } from 'react';
import { calendarAPI } from '../services/api';
import { useOffline } from '../context/OfflineContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  MapPin,
  Users,
  Trash2,
  CalendarDays,
  Bell,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  subWeeks,
  addHours,
  startOfDay,
  eachHourOfInterval,
} from 'date-fns';
import { fr } from 'date-fns/locale';

const colorOptions = [
  { value: '#0066DA', label: 'Bleu' },
  { value: '#22C55E', label: 'Vert' },
  { value: '#EF4444', label: 'Rouge' },
  { value: '#EAB308', label: 'Jaune' },
  { value: '#8B5CF6', label: 'Violet' },
  { value: '#F97316', label: 'Orange' },
];

const reminderOptions = [
  { value: 0, label: 'Aucun' },
  { value: 5, label: '5 minutes avant' },
  { value: 15, label: '15 minutes avant' },
  { value: 30, label: '30 minutes avant' },
  { value: 60, label: '1 heure avant' },
  { value: 1440, label: '1 jour avant' },
];

const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    all_day: false,
    location: '',
    reminder_minutes: 15,
    attendees: '',
    color: '#0066DA',
  });
  const reminderIntervalRef = useRef(null);
  const { isOnline, cacheEvents, getCachedEvents } = useOffline();

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const start = format(startOfMonth(subMonths(currentDate, 1)), 'yyyy-MM-dd');
      const end = format(endOfMonth(addMonths(currentDate, 1)), 'yyyy-MM-dd');
      const response = await calendarAPI.getEvents(start, end);
      setEvents(response.data.events);
      cacheEvents(response.data.events);
    } catch (error) {
      console.error('Failed to load events:', error);
      if (!isOnline) {
        const cached = await getCachedEvents();
        if (cached.length > 0) {
          setEvents(cached);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // Reminder checker
  useEffect(() => {
    const checkReminders = async () => {
      try {
        const res = await calendarAPI.getReminders();
        const reminders = res.data.reminders || [];
        for (const event of reminders) {
          toast.info(`Rappel : ${event.title}`, {
            description: event.location ? `Lieu : ${event.location}` : format(new Date(event.start_date), "HH:mm", { locale: fr }),
            duration: 10000,
            icon: <Bell className="w-4 h-4" />,
          });
          await calendarAPI.dismissReminder(event.id);
        }
      } catch {
        // Silent fail for reminders
      }
    };

    checkReminders();
    reminderIntervalRef.current = setInterval(checkReminders, 60000);
    return () => clearInterval(reminderIntervalRef.current);
  }, []);

  const handlePrev = () => {
    if (view === 'month') setCurrentDate(subMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, -1));
  };

  const handleNext = () => {
    if (view === 'month') setCurrentDate(addMonths(currentDate, 1));
    else if (view === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const openNewEvent = (date = selectedDate, hour = null) => {
    let startDate = date;
    if (hour !== null) {
      startDate = new Date(date);
      startDate.setHours(hour, 0, 0, 0);
    }
    const dateStr = format(startDate, "yyyy-MM-dd'T'HH:mm");
    const endDate = addHours(startDate, 1);
    setEventForm({
      title: '',
      description: '',
      start_date: dateStr,
      end_date: format(endDate, "yyyy-MM-dd'T'HH:mm"),
      all_day: false,
      location: '',
      reminder_minutes: 15,
      attendees: '',
      color: '#0066DA',
    });
    setEditingEvent(null);
    setDialogOpen(true);
  };

  const openEditEvent = (event) => {
    setEventForm({
      title: event.title,
      description: event.description || '',
      start_date: event.start_date,
      end_date: event.end_date,
      all_day: event.all_day || false,
      location: event.location || '',
      reminder_minutes: event.reminder_minutes || 15,
      attendees: event.attendees?.join(', ') || '',
      color: event.color || '#0066DA',
    });
    setEditingEvent(event);
    setDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title.trim()) {
      toast.error('Veuillez saisir un titre');
      return;
    }
    try {
      const data = {
        ...eventForm,
        attendees: eventForm.attendees ? eventForm.attendees.split(',').map(e => e.trim()).filter(Boolean) : [],
      };
      if (editingEvent) {
        await calendarAPI.updateEvent(editingEvent.id, data);
        toast.success('Événement modifié');
      } else {
        await calendarAPI.createEvent(data);
        toast.success('Événement créé');
      }
      setDialogOpen(false);
      loadEvents();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteEvent = async () => {
    if (!editingEvent) return;
    try {
      await calendarAPI.deleteEvent(editingEvent.id);
      toast.success('Événement supprimé');
      setDialogOpen(false);
      loadEvents();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getEventsForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return events.filter(event => {
      const eventDate = event.start_date.split('T')[0];
      return eventDate === dateStr;
    });
  };

  const getEventsForHour = (date, hour) => {
    return getEventsForDay(date).filter(event => {
      if (event.all_day) return false;
      try {
        const eventHour = new Date(event.start_date).getHours();
        return eventHour === hour;
      } catch {
        return false;
      }
    });
  };

  const getAllDayEvents = (date) => {
    return getEventsForDay(date).filter(event => event.all_day);
  };

  const getHeaderTitle = () => {
    if (view === 'month') return format(currentDate, 'MMMM yyyy', { locale: fr });
    if (view === 'week') {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(weekStart, 'd MMM', { locale: fr })} - ${format(weekEnd, 'd MMM yyyy', { locale: fr })}`;
    }
    return format(currentDate, 'EEEE d MMMM yyyy', { locale: fr });
  };

  // ============= MONTH VIEW =============
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: startDate, end: endDate });
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    return (
      <div className="flex-1 flex flex-col">
        <div className="grid grid-cols-7 border-b border-border">
          {weekDays.map((day) => (
            <div key={day} className="p-3 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        <div className="flex-1 grid grid-cols-7 auto-rows-fr">
          {days.map((day, index) => {
            const dayEvents = getEventsForDay(day);
            const isToday = isSameDay(day, new Date());
            const isSelected = isSameDay(day, selectedDate);
            const isCurrentMonth = isSameMonth(day, currentDate);
            return (
              <div
                key={index}
                className={cn(
                  "border-b border-r border-border p-1 min-h-[100px] cursor-pointer hover:bg-muted/50 transition-colors",
                  !isCurrentMonth && "bg-muted/30 text-muted-foreground"
                )}
                onClick={() => setSelectedDate(day)}
                onDoubleClick={() => openNewEvent(day)}
                data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
              >
                <div className={cn(
                  "w-7 h-7 flex items-center justify-center rounded-full text-sm mb-1",
                  isToday && "bg-primary text-primary-foreground",
                  isSelected && !isToday && "bg-primary/20"
                )}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-0.5">
                  {dayEvents.slice(0, 3).map((event) => (
                    <div
                      key={event.id}
                      className="text-xs px-1.5 py-0.5 rounded truncate text-white cursor-pointer"
                      style={{ backgroundColor: event.color || '#0066DA' }}
                      onClick={(e) => { e.stopPropagation(); openEditEvent(event); }}
                    >
                      {event.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-muted-foreground px-1.5">+{dayEvents.length - 3} autres</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ============= WEEK VIEW =============
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekDays = eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 6) });

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Week header */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border flex-shrink-0">
          <div className="p-2" />
          {weekDays.map((day) => (
            <div
              key={day.toISOString()}
              className={cn(
                "p-2 text-center border-l border-border cursor-pointer hover:bg-muted/50",
                isSameDay(day, new Date()) && "bg-primary/10"
              )}
              onClick={() => { setSelectedDate(day); setView('day'); }}
            >
              <div className="text-xs text-muted-foreground">{format(day, 'EEE', { locale: fr })}</div>
              <div className={cn(
                "text-lg font-semibold w-8 h-8 mx-auto flex items-center justify-center rounded-full",
                isSameDay(day, new Date()) && "bg-primary text-primary-foreground"
              )}>
                {format(day, 'd')}
              </div>
            </div>
          ))}
        </div>

        {/* All-day events row */}
        <div className="grid grid-cols-[60px_repeat(7,1fr)] border-b border-border flex-shrink-0">
          <div className="p-1 text-xs text-muted-foreground text-right pr-2">Journée</div>
          {weekDays.map((day) => {
            const allDayEvts = getAllDayEvents(day);
            return (
              <div key={day.toISOString()} className="border-l border-border p-0.5 min-h-[28px]">
                {allDayEvts.slice(0, 2).map((event) => (
                  <div
                    key={event.id}
                    className="text-xs px-1 py-0.5 rounded truncate text-white cursor-pointer mb-0.5"
                    style={{ backgroundColor: event.color || '#0066DA' }}
                    onClick={() => openEditEvent(event)}
                  >
                    {event.title}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Time grid */}
        <ScrollArea className="flex-1">
          <div className="grid grid-cols-[60px_repeat(7,1fr)]">
            {HOURS.map((hour) => (
              <React.Fragment key={hour}>
                <div className="h-14 text-xs text-muted-foreground text-right pr-2 pt-0.5 border-b border-border">
                  {`${String(hour).padStart(2, '0')}:00`}
                </div>
                {weekDays.map((day) => {
                  const hourEvents = getEventsForHour(day, hour);
                  return (
                    <div
                      key={`${day.toISOString()}-${hour}`}
                      className="h-14 border-l border-b border-border relative hover:bg-muted/30 cursor-pointer"
                      onClick={() => openNewEvent(day, hour)}
                    >
                      {hourEvents.map((event) => (
                        <div
                          key={event.id}
                          className="absolute inset-x-0.5 top-0.5 text-xs px-1 py-0.5 rounded text-white truncate cursor-pointer z-10"
                          style={{ backgroundColor: event.color || '#0066DA' }}
                          onClick={(e) => { e.stopPropagation(); openEditEvent(event); }}
                        >
                          {event.title}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </ScrollArea>
      </div>
    );
  };

  // ============= DAY VIEW =============
  const renderDayView = () => {
    const allDayEvts = getAllDayEvents(currentDate);

    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Day header */}
        <div className="p-3 border-b border-border flex-shrink-0">
          <div className={cn(
            "text-lg font-semibold",
            isSameDay(currentDate, new Date()) && "text-primary"
          )}>
            {format(currentDate, 'EEEE d MMMM', { locale: fr })}
          </div>
        </div>

        {/* All-day events */}
        {allDayEvts.length > 0 && (
          <div className="p-2 border-b border-border flex-shrink-0 space-y-1">
            <span className="text-xs text-muted-foreground">Toute la journée</span>
            {allDayEvts.map((event) => (
              <div
                key={event.id}
                className="text-sm px-2 py-1 rounded text-white cursor-pointer"
                style={{ backgroundColor: event.color || '#0066DA' }}
                onClick={() => openEditEvent(event)}
              >
                {event.title}
              </div>
            ))}
          </div>
        )}

        {/* Time slots */}
        <ScrollArea className="flex-1">
          <div className="grid grid-cols-[60px_1fr]">
            {HOURS.map((hour) => {
              const hourEvents = getEventsForHour(currentDate, hour);
              return (
                <React.Fragment key={hour}>
                  <div className="h-16 text-xs text-muted-foreground text-right pr-2 pt-0.5 border-b border-border">
                    {`${String(hour).padStart(2, '0')}:00`}
                  </div>
                  <div
                    className="h-16 border-b border-border relative hover:bg-muted/30 cursor-pointer"
                    onClick={() => openNewEvent(currentDate, hour)}
                  >
                    {hourEvents.map((event) => (
                      <div
                        key={event.id}
                        className="absolute inset-x-1 top-0.5 text-sm px-2 py-1 rounded text-white cursor-pointer z-10"
                        style={{ backgroundColor: event.color || '#0066DA' }}
                        onClick={(e) => { e.stopPropagation(); openEditEvent(event); }}
                      >
                        <div className="font-medium truncate">{event.title}</div>
                        {event.location && (
                          <div className="text-xs opacity-80 truncate flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {event.location}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </ScrollArea>
      </div>
    );
  };

  // ============= AGENDA VIEW =============
  const renderAgendaView = () => {
    const upcomingEvents = [...events]
      .filter(event => {
        try {
          const eventDate = new Date(event.start_date);
          return eventDate >= startOfDay(new Date());
        } catch { return false; }
      })
      .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    // Group by date
    const groupedByDate = {};
    for (const event of upcomingEvents) {
      const dateKey = event.start_date.split('T')[0];
      if (!groupedByDate[dateKey]) groupedByDate[dateKey] = [];
      groupedByDate[dateKey].push(event);
    }

    const dateKeys = Object.keys(groupedByDate).sort();

    return (
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="max-w-2xl mx-auto p-6 space-y-1">
            {dateKeys.length === 0 ? (
              <div className="text-center py-20 text-muted-foreground">
                <CalendarDays className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Aucun événement à venir</p>
                <p className="text-sm">Créez un événement pour commencer</p>
              </div>
            ) : (
              dateKeys.map((dateKey) => {
                const dayDate = new Date(dateKey + 'T00:00:00');
                const isToday = isSameDay(dayDate, new Date());
                return (
                  <div key={dateKey} className="flex gap-4 py-3">
                    <div className={cn(
                      "w-20 flex-shrink-0 text-right pt-1",
                      isToday && "text-primary"
                    )}>
                      <div className="text-sm font-medium capitalize">
                        {format(dayDate, 'EEE', { locale: fr })}
                      </div>
                      <div className={cn(
                        "text-2xl font-bold",
                        isToday && "text-primary"
                      )}>
                        {format(dayDate, 'd')}
                      </div>
                      <div className="text-xs text-muted-foreground capitalize">
                        {format(dayDate, 'MMM', { locale: fr })}
                      </div>
                    </div>
                    <div className="flex-1 space-y-2 border-l-2 border-border pl-4">
                      {groupedByDate[dateKey].map((event) => (
                        <div
                          key={event.id}
                          className="p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors group"
                          onClick={() => openEditEvent(event)}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ backgroundColor: event.color || '#0066DA' }} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium">{event.title}</p>
                              <p className="text-sm text-muted-foreground">
                                {event.all_day ? 'Toute la journée' :
                                  `${format(new Date(event.start_date), 'HH:mm')} - ${format(new Date(event.end_date), 'HH:mm')}`}
                              </p>
                              {event.location && (
                                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                  <MapPin className="w-3 h-3" />
                                  {event.location}
                                </p>
                              )}
                              {event.description && (
                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{event.description}</p>
                              )}
                              {event.reminder_minutes > 0 && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                  <Bell className="w-3 h-3" />
                                  {reminderOptions.find(o => o.value === event.reminder_minutes)?.label || `${event.reminder_minutes} min`}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>
    );
  };

  // ============= SIDEBAR =============
  const renderSidebar = () => {
    const dateToShow = view === 'day' ? currentDate : selectedDate;
    const dayEvents = getEventsForDay(dateToShow);

    return (
      <div className="w-80 border-l border-border flex flex-col flex-shrink-0">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold">
            {format(dateToShow, 'EEEE d MMMM', { locale: fr })}
          </h3>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-3">
            {dayEvents.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <CalendarDays className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun événement</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={() => openNewEvent(dateToShow)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>
            ) : (
              dayEvents.map((event) => (
                <div
                  key={event.id}
                  className="p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => openEditEvent(event)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: event.color || '#0066DA' }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{event.title}</p>
                      <p className="text-sm text-muted-foreground">
                        {event.all_day ? 'Toute la journée' :
                          `${format(new Date(event.start_date), 'HH:mm')} - ${format(new Date(event.end_date), 'HH:mm')}`
                        }
                      </p>
                      {event.location && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </p>
                      )}
                      {event.reminder_minutes > 0 && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Bell className="w-3 h-3" />
                          Rappel : {reminderOptions.find(o => o.value === event.reminder_minutes)?.label || `${event.reminder_minutes} min`}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col p-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] capitalize">
            {getHeaderTitle()}
          </h1>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={handlePrev} data-testid="calendar-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday} data-testid="calendar-today-btn">
              Aujourd'hui
            </Button>
            <Button variant="outline" size="icon" onClick={handleNext} data-testid="calendar-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Select value={view} onValueChange={setView}>
            <SelectTrigger className="w-32" data-testid="calendar-view-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="month">Mois</SelectItem>
              <SelectItem value="week">Semaine</SelectItem>
              <SelectItem value="day">Jour</SelectItem>
              <SelectItem value="agenda">Agenda</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => openNewEvent()} data-testid="new-event-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nouvel événement
          </Button>
        </div>
      </div>

      {/* Calendar body */}
      <div className="flex-1 flex border border-border rounded-lg overflow-hidden bg-card">
        {view === 'month' && renderMonthView()}
        {view === 'week' && renderWeekView()}
        {view === 'day' && renderDayView()}
        {view === 'agenda' && renderAgendaView()}
        {view !== 'agenda' && renderSidebar()}
      </div>

      {/* Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">
              {editingEvent ? "Modifier l'événement" : 'Nouvel événement'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input
                placeholder="Titre de l'événement"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                data-testid="event-title-input"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label>Toute la journée</Label>
              <Switch
                checked={eventForm.all_day}
                onCheckedChange={(v) => setEventForm({ ...eventForm, all_day: v })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Début</Label>
                <Input
                  type={eventForm.all_day ? "date" : "datetime-local"}
                  value={eventForm.start_date}
                  onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })}
                  data-testid="event-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Fin</Label>
                <Input
                  type={eventForm.all_day ? "date" : "datetime-local"}
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })}
                  data-testid="event-end-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Lieu</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Ajouter un lieu"
                  className="pl-10"
                  value={eventForm.location}
                  onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                  data-testid="event-location"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Ajouter une description"
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rappel</Label>
                <Select
                  value={String(eventForm.reminder_minutes)}
                  onValueChange={(v) => setEventForm({ ...eventForm, reminder_minutes: parseInt(v) })}
                >
                  <SelectTrigger data-testid="event-reminder-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {reminderOptions.map((opt) => (
                      <SelectItem key={opt.value} value={String(opt.value)}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Couleur</Label>
                <Select
                  value={eventForm.color}
                  onValueChange={(v) => setEventForm({ ...eventForm, color: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: opt.value }} />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Participants</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="email@exemple.com, autre@exemple.com"
                  className="pl-10"
                  value={eventForm.attendees}
                  onChange={(e) => setEventForm({ ...eventForm, attendees: e.target.value })}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            {editingEvent && (
              <Button variant="destructive" onClick={handleDeleteEvent} data-testid="delete-event-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveEvent} data-testid="save-event-btn">
                {editingEvent ? 'Enregistrer' : 'Créer'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
